import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useOutsideClick } from "../Utils/hooks";
import { debounce } from "Utils";
import { LoadingComponent } from "Components";

export const MultiCheckDropdown = ({
  loading = false,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}) => {
  const { t } = useTranslation();

  const dropdownRef = useRef(null);
  const chipsContainerRef = useRef(null);

  const [selectedOptions, setSelectedOptions] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [visibleOptions, setVisibleOptions] = useState([]);
  const [hiddenOptionCount, setHiddenOptionCount] = useState(0);

  useOutsideClick(dropdownRef, () => setDropdownVisible(false));

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedOptions(value);
    } else {
      setSelectedOptions([]);
    }
  }, [value]);

  useEffect(() => {
    calculateVisibleChips();
    window.addEventListener("resize", calculateVisibleChips);
    return () => window.removeEventListener("resize", calculateVisibleChips);
  }, [selectedOptions]);

  const calculateVisibleChips = debounce(() => {
    if (!chipsContainerRef.current) return;

    const containerWidth = chipsContainerRef.current.offsetWidth;
    let totalWidth = 0;
    let visible = [];
    let hidden = 0;
    let searchFieldWidth = 138.8 + 53.16;

    // Create a hidden canvas for measuring text widths
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    // Set the font style to match your selected option style
    context.font = "12px Roboto";

    // Measure widths of all selected options
    const chipWidths = selectedOptions.map((option) => {
      const width = context.measureText(option.label).width + 14; // Add padding
      return width;
    });

    // Calculate visible chips
    chipWidths.forEach((width, index) => {
      if (totalWidth + width + searchFieldWidth <= containerWidth) {
        totalWidth += width;
        visible.push(selectedOptions[index]);
      } else {
        hidden++;
      }
    });

    setVisibleOptions(visible);
    setHiddenOptionCount(hidden);
  }, 300);

  const handleOptionChange = (option) => {
    const selectedOptionIndex = selectedOptions?.findIndex(
      (selected) => selected.value === option.value
    );

    if (selectedOptionIndex > -1) {
      const updatedOptions = [...selectedOptions];
      updatedOptions.splice(selectedOptionIndex, 1);
      setSelectedOptions(updatedOptions);
      onChange(updatedOptions);
    } else {
      setSelectedOptions([...selectedOptions, option]);
      onChange([...selectedOptions, option]);
    }
  };

  const handleSelectAll = () => {
    if (selectedOptions?.length === options.length) {
      setSelectedOptions([]);
      onChange([]);
    } else {
      setSelectedOptions([...options]);
      onChange([...options]);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  // const filteredOptions =
  //   options &&
  //   options?.filter((option) =>
  //     option.label?.toLowerCase().includes(searchValue?.toLowerCase())
  //   );

  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];
    if (searchValue.trim() !== "") {
      return options.filter((option) =>
        option.label?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    return options;
  }, [options, searchValue]);

  return (
    <div className="select-group" ref={dropdownRef}>
      <div
        className={`selected-options d-flex align-items-center ${
          disabled ? "isDisabled" : ""
        }`}
        data-bs-auto-close="outside"
        ref={chipsContainerRef}
      >
        {visibleOptions.map((option, idx) => (
          <span
            key={`${option.value}-${idx}`}
            className="selected-option"
            style={{ whiteSpace: "nowrap" }}
          >
            {option.label}
            {!disabled && (
              <span
                className="remove-icon"
                onClick={() => handleOptionChange(option)}
              >
                &times;
              </span>
            )}
          </span>
        ))}
        {hiddenOptionCount > 0 && (
          <span
            className="more-indicator"
            style={{ whiteSpace: "nowrap", marginLeft: "5px" }}
          >
            +{hiddenOptionCount} More
          </span>
        )}
        <input
          type="text"
          className="search-input"
          placeholder={
            placeholder ? placeholder : t("common.placeHolder.search")
          }
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={() => setDropdownVisible(true)}
          disabled={disabled}
        />
      </div>
      {dropdownVisible && (
        <div className="select-dropdown position-absolute left-0 right-0 bg-white z-10 p-3 shadow">
          <LoadingComponent isLoading={loading}>
            <label>
              <input
                type="checkbox"
                checked={selectedOptions?.length === options.length}
                onChange={handleSelectAll}
                className="form-check-input me-2"
              />
              {t("common.label.selectAll")}
            </label>
            {filteredOptions &&
              filteredOptions.length > 0 &&
              filteredOptions.map((option, idx) => (
                <label key={`${option.value}-${idx}`}>
                  <input
                    type="checkbox"
                    checked={selectedOptions?.some(
                      (selected) => selected.value === option.value
                    )}
                    onChange={() => handleOptionChange(option)}
                    className="form-check-input me-2"
                  />
                  {option.label}
                </label>
              ))}
          </LoadingComponent>
        </div>
      )}
    </div>
  );
};
